import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";

export type TextProps = Omit<
  HTMLAttributes<
    HTMLParagraphElement | HTMLLIElement | HTMLHeadingElement | HTMLQuoteElement
  >,
  "className"
> & {
  children: ReactNode;
  variant?: VariantProps<typeof textVariants>["variant"];
  color?: "black" | "white" | "muted" | "success" | "red" | "warning";
  /** Style variants for text (e.g. 'line-through') */
  style?: VariantProps<typeof textVariants>["style"];
};

const textVariants = cva("", {
  variants: {
    variant: {
      h1: "block font-bold font-heading text-3xl leading-none tracking-tight md:text-4xl md:leading-none",
      h2: "block pb-2 font-heading font-semibold text-2xl text-blue leading-7 tracking-tight md:text-3xl md:leading-7",
      "h2-underline":
        "!border-neutral-300 !border-b-2 block pb-2 font-heading font-semibold text-2xl leading-7 tracking-tight md:text-3xl md:leading-7",
      h3: "block font-heading font-semibold text-xl leading-8 tracking-tight sm:text-2xl",
      "h3-light":
        "block font-heading font-normal text-2xl leading-8 tracking-tight",
      h4: "block font-heading font-semibold text-base leading-6 tracking-tight sm:text-xl sm:leading-6",
      "body-md": "font-normal text-base leading-5",
      "body-md-line-through": "font-normal text-base leading-5 line-through",
      "body-md-bold": "font-semibold text-base leading-7",
      "body-sm": "font-medium text-sm leading-4",
      "body-sm-bold": "font-semibold text-sm leading-4",
      "body-xs": "font-normal text-xs leading-4",
      "body-xs-bold": "font-semibold text-xs leading-4 tracking-normal",
      "body-lg": "font-semibold text-lg leading-7 tracking-normal",
      lead: "font-normal text-neutral-600 text-xl leading-7 tracking-normal",
      muted: "font-medium text-neutral-600 text-sm leading-4",
    },
    color: {
      black: "text-neutral-darker",
      white: "text-white",
      muted: "text-muted-foreground",
      success: "text-success",
      red: "text-red-600",
      warning: "text-orange-500",
    },
    style: {
      "line-through": "line-through",
    },
  },
  defaultVariants: {
    variant: "body-md",
    color: "black",
  },
});

// eslint-disable-next-line react/destructuring-assignment
export function Text({
  children,
  variant = "body-md",
  color = "black",
  style: styleVariant,
  ...props
}: TextProps) {
  const Tag = getTag(variant);
  const variantClasses = textVariants({ variant, color, style: styleVariant });

  return (
    <Tag
      className={variantClasses}
      {...(Tag === "h1"
        ? { id: "main-content", tabIndex: -1, ...props }
        : props)}
    >
      {children}
    </Tag>
  );
}

function getTag(
  variant: TextProps["variant"]
): "p" | "h1" | "h2" | "h3" | "h4" {
  switch (variant) {
    case "h1":
      return "h1";
    case "h2":
    case "h2-underline":
      return "h2";
    case "h3":
    case "h3-light":
      return "h3";
    case "h4":
      return "h4";
    case "body-md":
    case "body-md-bold":
    case "body-sm":
    case "body-sm-bold":
    case "body-xs":
    case "body-xs-bold":
    case "body-lg":
    case "lead":
    case "muted":
      return "p";
    default:
      return "p";
  }
}
